import React, {useMemo, useState} from "react";
import {
    Button,
    checkAndSetError,
    clearInputFormErrorFields,
    convertInputFormToObject,
    DateTimePickerField,
    FileUploadButton,
    InputForm,
    inputFormIsValid,
    NumberField,
    SelectField,
    SwitchField,
    TextArea,
    TextField,
    useInputForm,
} from "../../../../Components/Inputs";
import {GridCell, gridConstants} from "../../../../Components/Grid";
import {loanLibraryCatalogItemModel} from "./loanLibraryCatalogItemFactory";
import {allow} from "../../../../Components/Authorize";
import * as PolicyEvents from "../../../../Components/Authorize/policyEventsConstants";
import {LOAN_DURATION_LIST, LOAN_LIBRARY_MEDIA_FORMAT_LIST} from "../loanLibraryConstants";
import {LoanLibraryInventoryView} from "./LoanLibraryInventoryItem/LoanLibraryInventoryView";
import {PageTitle} from "../../../../Components/Landmarks/PageTitle";
import {error as notifyError} from "../../../../Components/Notifications/NotifyUser/notifyUserUtility";
import {config} from "../../../../Utilities/Api";

import type {ListItem} from "../../../../Utilities/Types/listUtilities";

interface LoanLibraryCatalogItemViewProps {
    handleReturn: () => void;
    handleSaveLoanLibraryCatalogItem: (data: Record<string, unknown>) => void;
    inventoryItems: Record<string, any>[];
    isEdit: boolean;
    loanLibraryCatalogItem: Record<string, any>;
    manufacturerList: ListItem[];
    onInventoryChange: (items: Record<string, unknown>[]) => void;
    vendorList: ListItem[];
}

export const LoanLibraryCatalogItemView = ({
                                               handleReturn,
                                               handleSaveLoanLibraryCatalogItem,
                                               inventoryItems,
                                               isEdit,
                                               loanLibraryCatalogItem,
                                               manufacturerList,
                                               onInventoryChange,
                                               vendorList,
                                           }: LoanLibraryCatalogItemViewProps) => {

    const pageTitle = !isEdit ? "Add Loan Library Item" : "Edit Loan Library Item";

    const canManageCatalog = allow(PolicyEvents.MANAGE_LOAN_LIBRARY_CATALOG);

    const catalogItemModel = useMemo(() => loanLibraryCatalogItemModel(loanLibraryCatalogItem), [loanLibraryCatalogItem]);
    const loanLibraryCatalogItemForm = useInputForm(catalogItemModel);
    const [isInventoryItemEditing, setIsInventoryItemEditing] = useState(false);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(
        loanLibraryCatalogItem.productImageUrl || null
    );
    const [removeImage, setRemoveImage] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(0);

    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/bmp"];
    const MAX_IMAGE_SIZE_BYTES = 2 * 1024 * 1024;

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            notifyError("Invalid image type. Allowed: JPEG, PNG, GIF, WebP, BMP.");
            e.target.value = "";
            return;
        }

        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            notifyError("Image must be 2 MB or smaller.");
            e.target.value = "";
            return;
        }

        setImageFile(file);
        setImagePreviewUrl(URL.createObjectURL(file));
        setRemoveImage(false);
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreviewUrl(null);
        setRemoveImage(true);
        setFileInputKey(prev => prev + 1);
    };


    const handleSubmitClick = () => {

         if (isInventoryItemEditing) {
            notifyError("Save or cancel inventory row changes before adding the item.");
            return;
        }

        clearInputFormErrorFields(loanLibraryCatalogItemForm);

        checkAndSetError([
            loanLibraryCatalogItemForm.accessNumber,
            loanLibraryCatalogItemForm.productName,
            loanLibraryCatalogItemForm.mediaFormat,
        ]);

        if (inputFormIsValid(loanLibraryCatalogItemForm)) {
            const convertedFormData = convertInputFormToObject(loanLibraryCatalogItemForm);
            const catalogItem = loanLibraryCatalogItemModel(convertedFormData) as Record<string, unknown>;
            catalogItem.inventoryItems = inventoryItems;

            if (imageFile) {
                catalogItem.productImage = imageFile;
            }
            if (removeImage) {
                catalogItem.removeImage = true;
            }

            handleSaveLoanLibraryCatalogItem(catalogItem);
        }
    };

    const handleInventoryChange = (newInventoryItems: Record<string, unknown>[]) => {
        onInventoryChange(newInventoryItems);
    };

    const handleCheckInSwitch = (event: unknown) => {
        const updatedLoanLibraryCatalogItemForm = {...loanLibraryCatalogItemForm};
        updatedLoanLibraryCatalogItemForm.isForInternalUse.value = String((event as React.ChangeEvent<HTMLInputElement>).target.checked);
        loanLibraryCatalogItemForm.setValue(updatedLoanLibraryCatalogItemForm);
    };


    return (
        <>
            <div className="loanLibraryCatalogItem">
                <PageTitle h1={pageTitle} />
                <InputForm name="loanLibraryCatalogItemForm" key={loanLibraryCatalogItem.loanLibraryInventoryId || 'new'}>
                <NumberField
                    {...loanLibraryCatalogItemForm.accessNumber}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}
                    isWholeNumberOnly
                    isPositiveNumberOnly
                />

                <TextField
                    {...loanLibraryCatalogItemForm.catalogNumber}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <TextField
                    {...loanLibraryCatalogItemForm.productName}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <TextField
                    {...loanLibraryCatalogItemForm.author}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <SelectField
                    {...loanLibraryCatalogItemForm.mediaFormat}
                    medium_columns={gridConstants.column.SIX}
                    options={LOAN_LIBRARY_MEDIA_FORMAT_LIST}
                    includeDefaultOption
                    disabled={!canManageCatalog}/>

                <SwitchField
                    {...loanLibraryCatalogItemForm.isForInternalUse}
                    name='toggleFull'
                    disabled={!canManageCatalog}
                    label="Is For Internal Use"
                    legend="Is For Internal Use"
                    onChange={handleCheckInSwitch}
                    medium_columns={gridConstants.column.SIX}
                />

                <TextArea
                    {...loanLibraryCatalogItemForm.description}
                    medium_columns={gridConstants.column.TWELVE}
                    disabled={!canManageCatalog}/>

                <TextField
                    {...loanLibraryCatalogItemForm.keywords}
                    medium_columns={gridConstants.column.TWELVE}
                    disabled={!canManageCatalog}/>

                <hr/>

                <SelectField
                    {...loanLibraryCatalogItemForm.manufacturerId}
                    label={"Manufacturer"}
                    defaultOption={"Select Manufacturer"}
                    defaultOptionValue={""}
                    options={manufacturerList}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <SelectField
                    {...loanLibraryCatalogItemForm.vendorId}
                    label={"Vendor"}
                    defaultOption={"Select Vendor"}
                    defaultOptionValue={""}
                    options={vendorList}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}

                />

                <DateTimePickerField
                    {...loanLibraryCatalogItemForm.publicationDate}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <DateTimePickerField
                    {...loanLibraryCatalogItemForm.dateOfPurchase}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <hr/>

                <NumberField
                    {...loanLibraryCatalogItemForm.cost}
                    isPositiveNumberOnly
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                <hr/>

                <TextField
                    {...loanLibraryCatalogItemForm.relatedItems}
                    medium_columns={gridConstants.column.TWELVE}
                    disabled={!canManageCatalog}/>

                <SelectField
                    {...loanLibraryCatalogItemForm.loanTerms}
                    medium_columns={gridConstants.column.SIX}
                    options={LOAN_DURATION_LIST}
                    includeDefaultOption
                    disabled={!canManageCatalog}/>

                <TextField
                    {...loanLibraryCatalogItemForm.accessoriesAndParts}
                    medium_columns={gridConstants.column.TWELVE}
                    disabled={!canManageCatalog}/>

                <TextArea
                    {...loanLibraryCatalogItemForm.notes}
                    medium_columns={gridConstants.column.TWELVE}
                    disabled={!canManageCatalog}/>

                <hr/>

                <GridCell medium_columns={gridConstants.column.TWELVE}>
                    <h3>Product Image</h3>
                </GridCell>
                <GridCell medium_columns={gridConstants.column.SIX}>
                    {imagePreviewUrl && (
                        <div className="loanLibraryCatalogItem__image-preview">
                            <img
                                src={imagePreviewUrl}
                                alt="Product"
                                className="loanLibraryCatalogItem__image-preview-img"
                            />
                        </div>
                    )}
                    {canManageCatalog && (
                        <div>
                            <div className="loanLibraryCatalogItem__file-upload-btn">
                                <FileUploadButton
                                    key={fileInputKey}
                                    name="productImage"
                                    label={imagePreviewUrl ? "Replace Image" : "Upload Image"}
                                    accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                                    onChange={handleImageChange}
                                />
                            </div>
                            {imagePreviewUrl && (
                                <Button
                                    isCollapsed
                                    label="Remove Image"
                                    name="btnRemoveImage"
                                    onClick={handleRemoveImage}
                                />
                            )}
                        </div>
                    )}
                </GridCell>

                <hr/>

                <GridCell medium_columns={gridConstants.column.TWELVE}>
                    <h3>What&apos;s New Settings</h3>
                </GridCell>
                <DateTimePickerField
                    {...loanLibraryCatalogItemForm.whatsNewStartDate}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>
                <DateTimePickerField
                    {...loanLibraryCatalogItemForm.whatsNewEndDate}
                    medium_columns={gridConstants.column.SIX}
                    disabled={!canManageCatalog}/>

                </InputForm>
            </div>

            {
                canManageCatalog &&
                <LoanLibraryInventoryView
                    loanLibraryInventoryItems={inventoryItems}
                    onInventoryChange={handleInventoryChange}
                    onInventoryEditingChanged={setIsInventoryItemEditing}
                    loanLibraryCatalogItemId={loanLibraryCatalogItem.loanLibraryCatalogId}
                    productName={loanLibraryCatalogItem.productName}
                />
            }

            <GridCell className="text-center">
                {
                    canManageCatalog &&
                    <Button
                        isPrimary isCollapsed
                        label={!isEdit ? 'Add Item' : 'Save Changes'}
                        name="btnSubmit"
                        onClick={handleSubmitClick}/>
                }
                <Button isCollapsed label="Cancel" name="btnCancel" onClick={handleReturn}/>
            </GridCell>
        </>
    );
};

